import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import usePromiseLoadFetcher from "../../../../../common/core-ui/usePromiseLoadFetcher.ts";
import { DirectoryProduct } from "common/api/decimal/methods/samplingContent.ts";
import { SearchStatus } from "../../types.ts";
import { SampleTitleResultsLoaderData } from "./sampleTitleResultsLoader.ts";
import { getSearchQuery } from "../../query.ts";
import { FilterValue } from "../filter/SampleFilter.tsx";
import { focusWithSetTabIndex } from "../../../../../common/utils/focus.ts";

interface ResultsState {
  offset: number;
  items: DirectoryProduct[];
  totalPages: number;
  isReady: boolean;
  reachedEnd: boolean;
  hasError: boolean;
}

export default function useSampleTitleResults(filters: FilterValue[] = []) {
  const [urlParams] = useSearchParams();
  const query = getSearchQuery(urlParams);
  const fetcher = usePromiseLoadFetcher<SampleTitleResultsLoaderData>();
  const lastFetchKey = useRef("");
  const filtersString = useMemo(() => filters.join(","), [filters]);
  const listRef = useRef<HTMLUListElement | null>(null);
  const previousItemsCountRef = useRef(0);
  const [programName, setProgramName] = useState<string | null>(null);

  // Use single state object to avoid multiple re-renders
  const [resultsState, setResultsState] = useState<ResultsState>({
    offset: 0,
    items: [],
    totalPages: 0,
    isReady: false,
    reachedEnd: false,
    hasError: false,
  });

  // Reset on query/filter change
  useEffect(() => {
    setResultsState({
      offset: 0,
      items: [],
      totalPages: 0,
      isReady: false,
      reachedEnd: false,
      hasError: false,
    });
    lastFetchKey.current = "";
    previousItemsCountRef.current = 0;
  }, [query, filtersString]);

  // Extract common error handling logic
  const handleSearchError = useCallback((pageNumber: number) => {
    if (pageNumber === 1) {
      setResultsState((prev) => ({ ...prev, isReady: true, hasError: true }));
    } else {
      setResultsState((prev) => ({
        ...prev,
        isReady: true,
        reachedEnd: true,
      }));
    }
  }, []);

  // Fetch data
  const fetchKey = `${query}|${filtersString}|${resultsState.offset}`;
  const shouldFetch =
    query && fetcher.state === "idle" && fetchKey !== lastFetchKey.current;

  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    lastFetchKey.current = fetchKey;

    const params = new URLSearchParams({
      q: query,
      offset: resultsState.offset.toString(),
    });

    if (filters.length > 0) {
      params.set("filters", filtersString);
    }

    fetcher
      .load(`/search/api/sample?${params.toString()}`)
      .then((result) => {
        // Don't process if data hasn't loaded yet
        if (!result.data) {
          return;
        }

        setProgramName(result.data.programName);
        const pageNumber = resultsState.offset + 1;

        // Check if response has error from the loader
        if (result.data.hasError) {
          handleSearchError(pageNumber);
          return;
        }
        if (result.data.results && result.data.results.length > 0) {
          setResultsState((prev) => ({
            ...prev,
            items: [...prev.items, ...result.data!.results],
            totalPages: result.data!.totalPages,
            isReady: true,
          }));
        } else {
          // API returned empty results (pageItems: 0)
          setResultsState((prev) => ({
            ...prev,
            isReady: true,
            reachedEnd: true,
          }));
        }
      })
      .catch(() => {
        const pageNumber = resultsState.offset + 1;
        handleSearchError(pageNumber);
      });
  }, [
    shouldFetch,
    fetchKey,
    query,
    resultsState.offset,
    filters,
    filtersString,
    fetcher,
    handleSearchError,
  ]);

  const status: SearchStatus = !resultsState.isReady
    ? "loading"
    : resultsState.hasError
    ? "error"
    : resultsState.items.length > 0
    ? "has-results"
    : "empty";

  const hasMore =
    !resultsState.reachedEnd &&
    resultsState.offset + 1 < resultsState.totalPages;
  const isLoadingMore =
    fetcher.state === "loading" && resultsState.items.length > 0;
  const isFetching = !resultsState.isReady || isLoadingMore;

  const loadMore = useCallback(() => {
    if (hasMore && !isLoadingMore) {
      previousItemsCountRef.current = resultsState.items.length;
      setResultsState((prev) => ({ ...prev, offset: prev.offset + 1 }));
    }
  }, [hasMore, isLoadingMore, resultsState.items.length]);

  // Focus first new result after loading more
  useEffect(() => {
    if (
      resultsState.isReady &&
      resultsState.items.length > previousItemsCountRef.current &&
      listRef.current
    ) {
      // Find the nth li element (where n is the previous count)
      const listItems = listRef.current.querySelectorAll("li");
      const firstNewItem = listItems[previousItemsCountRef.current];

      if (firstNewItem) {
        focusWithSetTabIndex(firstNewItem);
      }
    }
  }, [resultsState.isReady, resultsState.items.length]);

  return useMemo(
    () => ({
      status,
      items: resultsState.items,
      hasMore,
      loadMore,
      isLoadingMore,
      query,
      listRef,
      isFetching,
      programName,
    }),
    [
      status,
      resultsState.items,
      hasMore,
      loadMore,
      isLoadingMore,
      query,
      isFetching,
      programName,
    ]
  );
}
